import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Travel before the drag is claimed from whatever the sheet is scrolling.
 *
 * Deliberately small. `preventDefault` only cancels a scroll that has not started yet —
 * once iOS has committed the touch to one, every later call is ignored for the rest of
 * the gesture. So the direction has to be decided within the first move or two, not after
 * a comfortable margin.
 */
const ENGAGE_PX = 4

/** Past this much of the sheet's own height, letting go closes it. */
const CLOSE_FRACTION = 0.25

/** …or a downward flick, however short. Pixels per millisecond over the last move. */
const CLOSE_VELOCITY = 0.5

/** Long enough to read as the sheet leaving, short enough not to sit in the way. */
const SETTLE_MS = 200

/**
 * Whether the touch landed in something with room to scroll up, between the target and
 * the sheet itself.
 *
 * Every one of these sheets scrolls its own contents. A drag downward inside a list that
 * is scrolled away from its top is that list being scrolled back, not the sheet being
 * dismissed — so the sheet only takes the gesture once the content it holds has nowhere
 * left to go. This is the rule every native sheet uses, and without it a long action
 * list cannot be scrolled at all.
 */
const startsInScrolledContent = (target: EventTarget | null, sheet: HTMLElement): boolean => {
  let node = target instanceof Element ? target : null
  while (node && node !== sheet) {
    if (node.scrollTop > 0) return true
    node = node.parentElement
  }
  return false
}

/** Attach to the sheet's own element — MUI's paper slot, not the modal around it. */
type SheetRef = (node: HTMLElement | null) => void

interface IOptions {
  /**
   * False leaves the element alone. Pass a coarse-pointer check together with the
   * sheet's own open state — there is nothing to drag while it is closed, and a mouse
   * has the backdrop and the close control.
   */
  isEnabled: boolean
  /** Called once the drag has carried the sheet off the bottom of the screen. */
  onClose: () => void
}

/**
 * Drag a bottom sheet down to dismiss it.
 *
 * Every sheet on this surface already draws the grab handle that says you can — a
 * 36x4 pill at the top of MobileNav's More sheet, MobileControlsDrawer, FiltersModal and
 * Messages' ActionSheet — and none of them could actually be grabbed. An affordance that
 * does nothing is worse than none: it is the clearest single tell that a sheet is a
 * `<div>` pretending, since dismissing one by dragging is how every phone works.
 *
 * The offset is written as an inline transform rather than through a class or a custom
 * property, which is the one thing that works here: these are MUI Drawers, and MUI drives
 * its own slide-in by writing `transform` to the element's style attribute. A stylesheet
 * rule cannot outrank that, so the gesture has to speak the same way. On release the
 * sheet either finishes leaving — and stays where it is put, so MUI's exit has nothing
 * left to animate over — or springs back and hands the property back untouched.
 *
 * Returns a callback ref rather than taking a `RefObject`, and that is load-bearing. A
 * sheet lives in a portal that MUI mounts a commit *later* than the `open` it was handed:
 * an effect keyed on "the sheet is open" therefore runs while the paper does not exist
 * yet, reads null, attaches nothing, and never runs again — which is how this shipped
 * doing nothing at all. A callback ref fires when the node actually arrives, whenever
 * that is, and the listeners go on then.
 */
export const useSheetDismiss = ({ isEnabled, onClose }: IOptions): SheetRef => {
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [sheet, setSheet] = useState<HTMLElement | null>(null)

  // Held in a ref so a caller's inline callback doesn't re-run the effect below and
  // re-attach the listeners on every render of the sheet's contents.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const clearSheetState = useCallback((element: HTMLElement | null) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    if (!element) return
    element.style.transform = ''
    element.style.transition = ''
  }, [])

  useEffect(() => {
    const element = sheet
    if (!isEnabled || !element) return

    let origin: { x: number; y: number } | null = null
    let isEngaged = false
    let sheetHeight = 0
    let lastY = 0
    let lastAt = 0
    let velocity = 0

    const forget = () => {
      origin = null
      isEngaged = false
    }

    const handleTouchStart = (event: TouchEvent) => {
      // A gesture whose touchend never arrived must not keep cancelling moves in the sheet.
      if (isEngaged) clearSheetState(element)
      forget()
      // More than one finger is a pinch, never this.
      if (event.touches.length !== 1) return
      if (startsInScrolledContent(event.target, element)) return
      clearSheetState(element)

      const touch = event.touches[0]
      origin = { x: touch.clientX, y: touch.clientY }
      sheetHeight = element.getBoundingClientRect().height
      lastY = touch.clientY
      lastAt = event.timeStamp
      velocity = 0
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (!origin || sheetHeight === 0) return
      const touch = event.touches[0]
      const dx = touch.clientX - origin.x
      const dy = touch.clientY - origin.y

      if (!isEngaged) {
        // Upward is the content being scrolled, and sideways belongs to whatever is in
        // the sheet — an emoji catalog pages horizontally. Only a downward drag is this.
        if (dy < ENGAGE_PX) {
          if (Math.abs(dx) > ENGAGE_PX || dy < -ENGAGE_PX) forget()
          return
        }
        if (Math.abs(dx) > dy) {
          forget()
          return
        }
        isEngaged = true
        element.style.transition = 'none'
      }

      // Otherwise the page behind scrolls while the sheet moves, and the scroller
      // inside it takes the rest of the drag.
      event.preventDefault()
      event.stopPropagation()

      const elapsed = event.timeStamp - lastAt
      if (elapsed > 0) velocity = (touch.clientY - lastY) / elapsed
      lastY = touch.clientY
      lastAt = event.timeStamp

      // Never above its resting place: there is nothing above a bottom sheet to reveal,
      // and letting it lift shows a strip of backdrop under it.
      element.style.transform = `translateY(${Math.max(0, dy)}px)`
    }

    const handleTouchEnd = () => {
      if (!isEngaged || !origin) {
        forget()
        return
      }

      const travelled = lastY - origin.y
      const isClosing = travelled > sheetHeight * CLOSE_FRACTION || velocity > CLOSE_VELOCITY
      forget()

      element.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`

      if (!isClosing) {
        element.style.transform = 'translateY(0px)'
        closeTimer.current = setTimeout(() => clearSheetState(element), SETTLE_MS)
        return
      }

      // Carried the rest of the way and left there. `onClose` fires at the end rather
      // than now, so the sheet is gone before the screen behind it starts reacting —
      // and because the element is already off-screen, MUI's own exit has nothing left
      // to animate over.
      element.style.transform = 'translateY(100%)'
      closeTimer.current = setTimeout(() => {
        closeTimer.current = null
        onCloseRef.current()
      }, SETTLE_MS)
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })
    element.addEventListener('touchend', handleTouchEnd)
    element.addEventListener('touchcancel', handleTouchEnd)

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
      element.removeEventListener('touchcancel', handleTouchEnd)
      forget()
      clearSheetState(element)
    }
  }, [sheet, isEnabled, clearSheetState])

  return setSheet
}

export default useSheetDismiss
